import React, { useState, useEffect } from 'react';

const IntroductionIosPage = () => {
  const [animatedComponents, setAnimatedComponents] = useState({
    secure: false,
    verification: false,
    communication: false,
    shipping: false
  });

  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const triggerPosition = windowHeight * 4 / 5; // 3/4 from top of viewport

      // Get positions of each component relative to viewport
      const secureEl = document.getElementById('secure-transaction');
      const verificationEl = document.getElementById('verification-section');
      const communicationEl = document.getElementById('communication-section');
      const shippingEl = document.getElementById('shipping-section');

      if (secureEl) {
        const rect = secureEl.getBoundingClientRect();
        if (rect.top <= triggerPosition) {
          setAnimatedComponents(prev => ({ ...prev, secure: true }));
        }
      }
      if (verificationEl) {
        const rect = verificationEl.getBoundingClientRect();
        if (rect.top <= triggerPosition) {
          setAnimatedComponents(prev => ({ ...prev, verification: true }));
        }
      }
      if (communicationEl) {
        const rect = communicationEl.getBoundingClientRect();
        if (rect.top <= triggerPosition) {
          setAnimatedComponents(prev => ({ ...prev, communication: true }));
        }
      }
      if (shippingEl) {
        const rect = shippingEl.getBoundingClientRect();
        if (rect.top <= triggerPosition) {
          setAnimatedComponents(prev => ({ ...prev, shipping: true }));
        }
      }
    };

    // Wait for page to be fully loaded before adding scroll listener and checking positions
    const initializeScrollHandler = () => {
      window.addEventListener('scroll', handleScroll);
      handleScroll(); // Check initial position after page load
    };

    if (document.readyState === 'complete') {
      initializeScrollHandler();
    } else {
      window.addEventListener('load', initializeScrollHandler);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('load', initializeScrollHandler);
    };
  }, []);

  return (
    <div className="min-h-screen w-full bg-white overflow-x-hidden">
      <style jsx>{`
        img {
          user-drag: none;
          -webkit-user-drag: none;
        }
        .animated-orange-tag {
          clip-path: inset(0 99% 0 0);
          opacity: 0;
          transition: clip-path 500ms ease-out, opacity 500ms ease-out;
        }
        .animated-orange-tag.show {
          clip-path: inset(0 0 0 0);
          opacity: 1;
        }
      `}</style>
      
      {/* Hero Section */}
      <section className="w-full relative">
        <img
          src="/images/introduction/hero_phone.jpg"
          alt="DONDONスタートガイド for ビギナーズ"
          className="w-full h-auto object-cover"
          draggable="false"
        />
      </section>

      {/* Steps Title Section */}
      <section className="w-full relative bg-[#f4f4f4] overflow-hidden">
        <img
          src="/images/introduction/intro_bg_phone.png"
          alt="Background"
          className="w-full h-auto object-cover"
          draggable="false"
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-full px-4 pb-6 text-center">
            <h1 className="text-base font-bold text-white mb-1" style={{textShadow: '0px 0px 10px rgba(32, 48, 88, 1.0)'}}>
              売りたい！買いたい！かんたん取引ステップ
            </h1>
            <p className="text-sm font-bold text-white" style={{textShadow: '0px 0px 10px rgba(32, 48, 88, 1.0)'}}>
              DONDONの取引は簡単・安全・安心！
            </p>
          </div>
        </div>
      </section>

      {/* Trading Steps Section */}
      <section className="relative bg-[#f4f4f4] pt-3.5 pb-6">
        {/* Connecting Lines - Under all views */}
        <div className="absolute inset-0 top-20 bottom-20 pointer-events-none">
          <div className="w-full h-full relative">
            {/* Seller line - Left half */}
            <div className="absolute left-1/4 top-0 bottom-[800px] w-[9px] bg-[#3c5493] transform -translate-x-1/2"></div>
            {/* Buyer line - Right half */}
            <div className="absolute right-1/4 top-0 bottom-[800px] w-[9px] bg-[#ff6a00] transform translate-x-1/2"></div>
            {/* Additional connecting lines for later sections */}
            <div className="absolute left-1/4 bottom-[150px] h-[400px] w-[9px] bg-[#3c5493] transform -translate-x-1/2 z-[12]"></div>
            <div className="absolute right-1/4 bottom-0 h-[300px] w-[9px] bg-[#ff6a00] transform translate-x-1/2 z-[12]"></div>
          </div>
        </div>
        
        {/* Seller and Buyer Icons */}
        <div className="flex relative z-10 mb-8">
          {/* Left half - Seller */}
          <div className="w-1/2 flex justify-center">
            <div className="flex flex-col items-center gap-2.5">
              <div className="py-[5px] px-4 bg-[#3c5493] rounded-[17px] flex justify-center items-center">
                <span className="text-base font-bold text-white">売りたい人</span>
              </div>
              <img
                src="/images/introduction/seller_phone.png"
                alt="売りたい人"
                className="w-[114px] h-[115px]"
                draggable="false"
              />
            </div>
          </div>
          {/* Right half - Buyer */}
          <div className="w-1/2 flex justify-center">
            <div className="flex flex-col items-center gap-2.5">
              <div className="py-[5px] px-4 bg-[#ff6a00] rounded-[17px] flex justify-center items-center">
                <span className="text-base font-bold text-white">買いたい人</span>
              </div>
              <img
                src="/images/introduction/buyer_phone.png"
                alt="買いたい人"
                className="w-[114px] h-[115px]"
                draggable="false"
              />
            </div>
          </div>
        </div>

        {/* Step 1: Listing */}
        <div className="flex flex-col gap-2.5 mb-6 relative z-10">
          <div className="w-[248px] bg-[#d2def8] rounded-tr-xl rounded-br-xl border-r-2 border-b-2 border-[#3c5493] overflow-hidden">
            <div className="px-4 pt-4 pb-3.5 flex justify-center items-center">
              <h2 className="text-[#3c5493] text-base font-bold">商品を出品する</h2>
            </div>
            <div className="w-[246px] px-4 pt-3.5 pb-4 bg-white rounded-br-xl">
              <p className="text-sm leading-[21px]">
                売りたい商品を選び、<span className="text-[#3c5493] font-bold">状態や価格を入力</span>します。<br/>
                項目を選ぶだけのカンタン出品！
              </p>
            </div>
          </div>
        </div>

        {/* Step 1: Purchase */}
        <div className="flex flex-col gap-2.5 mb-6 relative z-10 items-end">
          <div className="w-[248px] bg-[#fce0ca] rounded-tl-xl rounded-bl-xl border-b-2 border-[#ff6a00] overflow-hidden">
            <div className="px-4 pt-4 pb-3.5 flex justify-center items-center">
              <h2 className="text-[#ff6a00] text-base font-bold">最安値で購入する</h2>
            </div>
            <div className="w-[246px] px-4 pt-3.5 pb-4 bg-white rounded-bl-xl">
              <p className="text-sm leading-[21px]">
                欲しい商品を選ぶだけで、<span className="text-[#ff6a00] font-bold">自動的に最安値で購入</span><span className="text-[#ff6a00]">！</span><br/>
                鑑定済の正規品が手に入ります
              </p>
            </div>
          </div>
        </div>

        {/* Secure Transaction Section */}
        <div className="px-4 mb-6 relative z-10">
          <div className="w-full p-4 bg-white rounded-xl border-r-2 border-b-2 border-[#aaaaaa] flex flex-col items-center gap-2.5">
            <div className="px-2.5 pb-px relative flex justify-center items-center">
              <div id="secure-transaction" className={`animated-orange-tag w-[284px] h-[25px] bg-[#ff6a00] flex justify-center items-center ${animatedComponents.secure ? 'show' : ''}`}>
                <h2 className="text-base font-bold text-white">DONDONが仲介するから安心安全！</h2>
              </div>
            </div>
            <img
              src="/images/introduction/steps_phone.jpg"
              alt="取引の流れ"
              className="w-full h-auto max-w-[311px]"
              draggable="false"
            />
            <p className="text-sm leading-[21px] text-left w-full">
              商品代金はDONDON運営本部で一時保管し、<br/>
              <span className="text-[#3c5493] font-bold">商品の鑑定後に出品者へ送金</span>されます。<br/>
              購入者の決済は<span className="text-[#ff6a00] font-bold">商品発送後に確定</span>となります。
            </p>
          </div>
        </div>

        {/* Step 2: Shipping */}
        <div className="flex flex-col gap-2.5 mb-6 relative z-10">
          <div className="w-[248px] bg-[#d2def8] rounded-tr-xl rounded-br-xl border-r-2 border-b-2 border-[#3c5493] overflow-hidden">
            <div className="px-4 pt-4 pb-3.5 flex justify-center items-center">
              <h2 className="text-[#3c5493] text-base font-bold">商品を発送する</h2>
            </div>
            <div className="w-[246px] px-4 pt-3.5 pb-4 bg-white rounded-br-xl">
              <p className="text-sm leading-[21px]">
                商品をDONDONへ発送。<br/>
                <span className="text-[#3c5493] font-bold">住所を購入者に知られることなく</span>完了！
              </p>
            </div>
          </div>
        </div>

        {/* Verification Section */}
        <div className="px-4 mb-6 relative">
          <div className="w-full p-4 bg-white rounded-xl border-r-2 border-b-2 border-[#aaaaaa] flex flex-col items-end gap-3.5">
            <div className="w-full flex flex-col items-center gap-2.5">
              <img
                src="/images/introduction/icon.png"
                alt="DONDONアイコン"
                className="w-[133.72px] h-[99px] object-contain"
                draggable="false"
              />
            </div>
            
            {/* Chat Section */}
            <div className="w-full px-4 pt-4 bg-[#f4f4f4] rounded-xl flex flex-col items-center gap-2.5">
              <div className="px-2.5 pb-px relative flex justify-center items-center">
                <div id="communication-section" className={`animated-orange-tag w-[132px] h-[25px] bg-[#ff6a00] flex justify-center items-center ${animatedComponents.communication ? 'show' : ''}`}>
                  <h3 className="text-base font-bold text-white">取引のやり取り</h3>
                </div>
              </div>
              <div className="w-full flex justify-center items-start gap-2.5">
                <img
                  src="/images/introduction/chat_phone.png"
                  alt="チャット"
                  className="w-24 h-[142px]"
                  draggable="false"
                />
                <div className="flex-1 pb-4">
                  <p className="text-sm leading-[21px]">
                    疑問や悩みなどは<span className="text-[#ff6a00] font-bold">チャットで質問</span>することができます。
                    DONDONスタッフがご対応させていただくため、
                    お気軽にご利用ください。
                  </p>
                </div>
              </div>
            </div>

            {/* Verification Section */}
            <div className="w-full p-4 bg-[#f4f4f4] rounded-xl flex flex-col items-center gap-2.5 z-20">
              <div className="px-2.5 pb-px relative flex justify-center items-center">
                <div id="verification-section" className={`animated-orange-tag w-[203px] h-[25px] bg-[#ff6a00] flex justify-center items-center ${animatedComponents.verification ? 'show' : ''}`}>
                  <h3 className="text-base font-bold text-white">DONDONによる真贋鑑定</h3>
                </div>
              </div>
              <div className="w-full flex justify-start items-start gap-2.5">
                <img
                  src="/images/introduction/verify1_phone.jpg"
                  alt="鑑定作業"
                  className="flex-1 h-[100px] object-cover rounded"
                  draggable="false"
                />
                <img
                  src="/images/introduction/verify2_phone.jpg"
                  alt="鑑定シール"
                  className="flex-1 h-[100px] object-cover rounded"
                  draggable="false"
                />
              </div>
              <p className="text-sm leading-[21px] text-left w-full">
                取引される商品はすべてDONDONの<span className="text-[#ff6a00] font-bold">プロ鑑定士が真贋鑑定</span>を行います。
                正規品と認定された商品にはその証明として鑑定済シールを貼付します。
                万が一、正規品と鑑定された商品が偽造品だった場合は、商品の購入価格を上限なく<span className="text-[#ff6a00] font-bold">全額補償</span>します。
              </p>
            </div>

            {/* Shipping Section */}
            <div className="w-[216px] p-4 bg-[#f4f4f4] rounded-xl flex flex-col items-center gap-2.5 z-20">
              <div className="px-2.5 pb-px relative flex justify-center items-center">
                <div id="shipping-section" className={`animated-orange-tag w-[188px] h-[25px] bg-[#ff6a00] flex justify-center items-center ${animatedComponents.shipping ? 'show' : ''}`}>
                  <h3 className="text-base font-bold text-white">DONDONから商品発送</h3>
                </div>
              </div>
              <p className="text-sm leading-[21px] text-left w-full">
                商品を購入者へ発送します。<br/>発送はDONDONが行うため、
                出品者・購入者ともに<span className="text-[#ff6a00] font-bold">住所を相手に知られることはありません！</span>
              </p>
            </div>
          </div>
        </div>

        {/* Final Steps */}
        {/* Payment Request */}
        <div className="flex flex-col gap-2.5 mb-6 relative z-20">
          <div className="w-[248px] bg-[#d2def8] rounded-tr-xl rounded-br-xl border-r-2 border-b-2 border-[#3c5493] overflow-hidden">
            <div className="px-4 pt-4 pb-3.5 flex justify-center items-center">
              <h2 className="text-[#3c5493] text-base font-bold">振込申請</h2>
            </div>
            <div className="w-[246px] px-4 pt-3.5 pb-4 bg-white rounded-br-xl">
              <p className="text-sm leading-[21px]">
                鑑定通過後、振込申請が可能となります
              </p>
            </div>
          </div>
        </div>

        {/* Final Receipt */}
        <div className="flex flex-col gap-2.5 relative z-20 items-end">
          <div className="w-[248px] bg-[#fce0ca] rounded-tl-xl rounded-bl-xl border-b-2 border-[#ff6a00] overflow-hidden">
            <div className="px-4 pt-4 pb-3.5 flex justify-center items-center">
              <h2 className="text-[#ff6a00] text-base font-bold">商品を受け取る</h2>
            </div>
            <div className="w-[246px] px-4 pt-3.5 pb-4 bg-white rounded-bl-xl">
              <p className="text-sm leading-[21px]">
                商品を受け取れば取引はすべて完了です。
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default IntroductionIosPage;
